<template>
    <section class="relative pt-8 overflow-hidden md:pt-16 font-ww-regular">
      <nuxt-img
        sizes="xs:320px sm:440px md:768px lg:1024px xl:1200px 2xl:1400px"
        class="absolute bottom-0 hidden object-cover w-full h-full top-20 md:top-32 md:block"
        src="/assets/images/bg.png"
        :alt="backgroundImgAlt"
        width="1920"
        height="1024"
        format="webp"
        loading="lazy"
      />
      <nuxt-img
        sizes="xs:320px sm:440px md:768px lg:620px xl:620px 2xl:620px"
        class="absolute bottom-0 object-cover w-full h-full top-32 md:top-32 md:hidden"
        src="/assets/images/gradient.png"
        :alt="gradientImgAlt"
        width="620"
        height="1700"
        format="webp"
        loading="lazy"
      />
  
      <div class="relative z-10 px-4 mx-auto max-w-desktop lg:px-8 2xl:px-56 sm:px-8">
        <div class="grid grid-cols-12 pb-12 md:pb-16 xl:gap-x-4 md:gap-x-8 lg:mt-10">
          <div class="order-2 col-span-12 md:col-span-7 md:order-1">
            <h3 class="hidden font-bold leading-none text-48 font-ww-medium text-lifeboost-gray-lighter md:block sm:text-58">
              Coffee, But Better
            </h3>
            <div
              class="px-4 mt-10 text-center text-25 sm:text-28 md:mt-10 md:text-28 leading-32 sm:leading-41 md:leading-38 font-ww-regular md:text-left lg:text-30 md:px-0 text-lifeboost-gray-lighter"
            >
              Get greater energy, better focus, and fewer jitters with the clean, healthy coffee upgraded with two powerful,
              brain-boosting mushrooms. <br /><br />
              Mushrooms contain beta-glucans, polysaccharides that support cells and tissues, improve energy storage, and
              stimulate the immune system. When you combine high-quality concentrated mushroom extracts from 100% real
              mushrooms, you get all the benefits of coffee enhanced to provide even more energy and mental power.
            </div>
          </div>
  
          <div class="col-span-12 mt-10 md:col-span-5 md:order-2 lg:mt-6 md:mt-0">
            <h3 class="font-bold text-center title-mushroom text-lifeboost-gray-lighter md:hidden">
              Coffee, But Better
            </h3>
            <nuxt-img
              sizes="xs:340px sm:382px md:544px lg:372px xl:400px 2xl:492px"
              src="/assets/images/Layer-1231.png"
              :alt="mainImageAlt"
              class="w-full mx-auto mt-10 md:mt-10 xl::absolute 2xl:w-[592px]"
              width="592"
              height="448"
              format="webp"
              loading="lazy"
            />
          </div>
        </div>
  
        <div class="max-w-screen-xl mx-auto mt-8 lg:mb-20 lg:mt-0">
          <div class="grid grid-cols-12 md:grid-cols-10 gap-x-0 md:gap-x-6 md:gap-y-20 gap-y-20">
            <div
              class="flex flex-col items-center col-span-6 md:col-span-2 md:gap-y-8 gap-y-8"
              v-for="(item, index) in items"
              :key="index"
            >
              <div class="bg-[#f3efed] rounded-full p-4 w-24 h-24 flex justify-center items-center">
                <nuxt-img
                  :src="item.imgSrc"
                  :alt="item.imgAlt"
                  class="object-contain"
                  :class="index == 1 ? 'w-9' : ''"
                  width="47"
                  height="47"
                  format="webp"
                  loading="lazy"
                />
              </div>
              <div class="leading-none text-center font-ww-regular text-lifeboost-gray-lighter text-28" v-html="item.text">
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>
  
  <script setup>
  const props = defineProps({
    items: {
      type: Array,
      default: () => [
        {
          text: 'Increased <br> Focus *',
          imgSrc: '/assets/images/item1.png',
          imgAlt: 'Increased Focus Icon',
        },
        {
          text: 'More <br> Energy *',
          imgSrc: '/assets/images/item2.png',
          imgAlt: 'More Energy Icon',
        },
        {
          text: 'No <br> Jitters *',
          imgSrc: '/assets/images/item3.png',
          imgAlt: 'No Jitters Icon',
        },
        {
          text: 'Brain <br> Health *',
          imgSrc: '/assets/images/item4.png',
          imgAlt: 'Brain Health Icon',
        },
        {
          text: 'Antioxidants',
          imgSrc: '/assets/images/item5.png',
          imgAlt: 'Antioxidants Icon',
        },
        {
          text: 'Better <br> Skin *',
          imgSrc: '/assets/images/item6.png',
          imgAlt: 'Better Skin Icon',
        },
        {
          text: 'Immune <br> System',
          imgSrc: '/assets/images/item7.png',
          imgAlt: 'Immune System Icon',
        },
        {
          text: 'Supports <br> Inflammatory Health *',
          imgSrc: '/assets/images/item8.png',
          imgAlt: 'Supports Inflammatory Health Icon',
        },
        {
          text: 'Lower <br> Stress *',
          imgSrc: '/assets/images/item9.png',
          imgAlt: 'Lower Stress Icon',
        },
        {
          text: 'Memory <br> Boost *',
          imgSrc: '/assets/images/item10.png',
          imgAlt: 'Memory Boost Icon',
        },
      ],
    },
    backgroundImgAlt: {
      type: String,
      default: 'Background gradient',
    },
    gradientImgAlt: {
      type: String,
      default: 'Mobile background gradient',
    },
    mainImageAlt: {
      type: String,
      default: 'Main Coffee Image',
    },
  });
  
  defineBuilderComponent({
    friendlyName: 'Description With Icons',
    inputs: [
      {
        name: 'items',
        type: 'list',
        friendlyName: 'Description Items List',
        subFields: [
          {
            name: 'text',
            type: 'richText',
            friendlyName: 'Text',
            defaultValue: '',
          },
          {
            name: 'imgSrc',
            type: 'file',
            friendlyName: 'Image Source',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
            defaultValue: '',
          },
          {
            name: 'imgAlt',
            type: 'string',
            friendlyName: 'Image Alt Text',
            defaultValue: 'Image Icon',
          },
        ],
        defaultValue: [
          {
            text: 'Increased <br> Focus *',
            imgSrc: '/assets/images/item1.png',
            imgAlt: 'Increased Focus Icon',
          },
          {
            text: 'More <br> Energy *',
            imgSrc: '/assets/images/item2.png',
            imgAlt: 'More Energy Icon',
          },
          {
            text: 'No <br> Jitters *',
            imgSrc: '/assets/images/item3.png',
            imgAlt: 'No Jitters Icon',
          },
          {
            text: 'Brain <br> Health *',
            imgSrc: '/assets/images/item4.png',
            imgAlt: 'Brain Health Icon',
          },
          {
            text: 'Antioxidants',
            imgSrc: '/assets/images/item5.png',
            imgAlt: 'Antioxidants Icon',
          },
          {
            text: 'Better <br> Skin *',
            imgSrc: '/assets/images/item6.png',
            imgAlt: 'Better Skin Icon',
          },
          {
            text: 'Immune <br> System',
            imgSrc: '/assets/images/item7.png',
            imgAlt: 'Immune System Icon',
          },
          {
            text: 'Supports <br> Inflammatory Health *',
            imgSrc: '/assets/images/item8.png',
            imgAlt: 'Supports Inflammatory Health Icon',
          },
          {
            text: 'Lower <br> Stress *',
            imgSrc: '/assets/images/item9.png',
            imgAlt: 'Lower Stress Icon',
          },
          {
            text: 'Memory <br> Boost *',
            imgSrc: '/assets/images/item10.png',
            imgAlt: 'Memory Boost Icon',
          },
        ],
      },
      {
        name: 'backgroundImgAlt',
        type: 'string',
        friendlyName: 'Background Image Alt Text',
        defaultValue: 'Background gradient',
      },
      {
        name: 'gradientImgAlt',
        type: 'string',
        friendlyName: 'Mobile Background Gradient Alt Text',
        defaultValue: 'Mobile background gradient',
      },
      {
        name: 'mainImageAlt',
        type: 'string',
        friendlyName: 'Main Image Alt Text',
        defaultValue: 'Main Coffee Image',
      },
    ],
  });
  </script>
  